import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { del } from 'idb-keyval';
import { useAuth } from '../contexts/AuthContext';
import { workspaceAPI } from '../services/api';
import Button from '../components/ui/Button';

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { isAuthenticated, loading, user } = useAuth();
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const accept = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await workspaceAPI.acceptInvite(token);
      client.clear();
      await del('leadmarka-query-cache');
      localStorage.removeItem('user');
      window.location.assign('/leads');
    } catch (err) { setError(err.response?.data?.error || 'Could not accept invitation'); setBusy(false); }
  };
  if (loading) return <p role="status">Loading…</p>;
  return <main className="max-w-lg mx-auto p-6 mt-12 bg-white border rounded-xl space-y-4">
    <h1 className="text-2xl font-bold">Join a workspace</h1>
    {!token ? <p role="alert">This invitation link is incomplete.</p> : <>
      <p>Accept using the email address that received the invitation. Invitations expire after seven days.</p>
      <div className="rounded-lg bg-primary-50 p-3 space-y-2 text-sm text-gray-700">
        <p>You’ll join as a member and work with the team’s shared contacts, enquiries, and conversations. The owner manages your permissions and the workspace subscription.</p>
        <p>Each account can belong to one workspace at a time. If you already belong to an active workspace, joining another will be blocked.</p>
        {isAuthenticated && user?.workspaceCompanyName && <p className="font-medium">Your current workspace: {user.workspaceCompanyName}</p>}
      </div>
      {isAuthenticated ? <><p>Signed in as {user?.email}</p><Button disabled={busy} onClick={accept}>{busy ? 'Joining…' : 'Accept invitation'}</Button></> : <div className="flex gap-4">
        <Link className="text-primary-700" to={`/login?invite=${encodeURIComponent(token)}`}>Sign in to accept</Link>
        <Link className="text-primary-700" to={`/register?invite=${encodeURIComponent(token)}`}>Create an account</Link>
      </div>}
    </>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
    {(!token || error) && <p className="text-sm text-gray-600">Check that you’re using the invited email address. For an incomplete, expired, or cancelled link, ask the owner for a new invitation. A new invitation does not remove the one-workspace limit. <Link className="compact-link text-primary-700 underline" to="/login">Return to sign in</Link></p>}
  </main>;
}
